import {useMutation} from '@tanstack/react-query'
import {useSnackbar} from 'notistack'
import {useState} from 'react'
import {useAuth} from 'src/contexts/AuthContext'
import {COMMON_DOUBLE_EXTENSIONS} from 'src/pages/camera-config/components/object-detection/components/stepperModal/components/Constants'
import api from 'src/services/api'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'

const TEN_MB = 10 * 1024 * 1024

export default function useUploadDataset() {
  const {enqueueSnackbar} = useSnackbar()
  const [progress, setProgress] = useState(0)
  const [eta, setEta] = useState<number | null>(null)
  const {user: currentUser} = useAuth()

  function getFileExtension(fileName: string): string {
    const parts = fileName.split('.')
    if (parts.length <= 1) return ''
    if (parts[parts.length - 1].length === 0) return ''

    const lastPart = parts[parts.length - 1]
    const secondLastPart = parts[parts.length - 2]

    const commonDoubleExtensions = COMMON_DOUBLE_EXTENSIONS
    const doubleExt = `${secondLastPart}.${lastPart}`
    if (commonDoubleExtensions.includes(doubleExt)) return doubleExt

    return lastPart.toLowerCase()
  }

  const mutation = useMutation({
    mutationFn: async (file: File) => {
      const fileExt = getFileExtension(file.name)
      const uuid = crypto.randomUUID()
      const fileName = `${currentUser.id}_${uuid}.${fileExt}`
      setProgress(0)
      setEta(null)

      const startRes = await api.company.startMultipart({
        fileName: fileName,
        fileType: file.type,
      })
      const uploadId = startRes?.data?.data?.uploadId
      if (!uploadId) throw new Error('Upload ID não encontrado')

      const CHUNK_SIZE = TEN_MB
      const parts: {ETag: string; PartNumber: number}[] = []
      const totalParts = Math.ceil(file.size / CHUNK_SIZE)

      const startTime = Date.now()

      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * CHUNK_SIZE
        const end = Math.min(start + CHUNK_SIZE, file.size)
        const blob = file.slice(start, end)

        const urlResponse = await api.company.getPartUploadUrl({
          fileName: fileName,
          partNumber,
          uploadId,
        })
        const url = urlResponse.data.data.partUrl
        if (!url) throw new Error('URL de upload não encontrada')

        const response = await api.awsService.uploadBlob(blob, url)
        const etag = response.headers['etag'] || response.headers['ETag'] || ''
        parts.push({ETag: etag, PartNumber: partNumber})

        setProgress(Math.round((partNumber / totalParts) * 100))

        const elapsed = (Date.now() - startTime) / 1000
        const avgTimePerPart = elapsed / partNumber
        const remainingParts = totalParts - partNumber
        setEta(Math.round(avgTimePerPart * remainingParts))
      }

      await api.company.completeMultipartUpload({
        fileName: fileName,
        uploadId,
        parts,
      })

      setEta(0)
    },
    onError: (error) => {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao fazer upload do arquivo')
    },
  })

  return {...mutation, progress, eta}
}
